/**
 * As correlações do CPTu: as 27 colunas da aba «Estimations» do CPeT-IT (k, cₕ, N60, M, Dr, φ',
 * Es, G₀, Nkt, Su, Su/σ'v, kOCR, OCR, Vs, ψ, K₀, St, φ' de pico, resistência ao cisalhamento, Kc,
 * Qtn,cs, su(liq)/σ'v, γ, w, e, γd e n) mais N1(60) — spec 07 §5.2, item 4; RN-252.
 *
 * As fórmulas são as da lista do CPeT-IT (a última folha do relatório 2609350) e das referências
 * dela — Robertson & Cabal (2012), Robertson (2009), Barounis & Philpot (2017) — e, para Kc, φ' e
 * a resistência liquefeita, de Robertson (2022), «Evaluation of flow liquefaction and liquefied
 * strength using the cone penetration test: an update», Can. Geotech. J., doi:10.1139/cgj-2020-0657
 * (versão online consultada; volume e páginas impressos não conferidos), e de Robertson & Cabal,
 * «Guide to Cone Penetration Testing», 7ª ed. (2022). Entram o básico (`basico.ts`, sobre a média
 * móvel de 3 leituras) e a zona SBTn (`classificacao.ts`). Unidades do motor (`unidades.ts`): qt
 * em MPa; tensões, Su e fs em kPa; M, G₀ e Es em MPa; k em m/s; cₕ em m²/s; Vs em m/s; ângulos em
 * graus; Dr e w em %.
 *
 *   k   = 10^(0,952 − 3,04·Ic) (1 < Ic ≤ 3,27) · 10^(−4,52 − 1,37·Ic) (3,27 < Ic ≤ 4)
 *   cₕ  = k·M/γw (o do perfil; o da dissipação é outro, `dissipacao.ts`)
 *                                                  N60 = (qt/pa)/10^(1,1268 − 0,2817·Ic)
 *   N1(60) = Qtn/10^(1,1268 − 0,2817·Ic)  (a folha; o gabarito não tem a coluna)
 *   M   = αM·(qt − σv), αM = mín(Qtn, 14) (Ic > 2,20) ou 0,0188·10^(0,55·Ic + 1,68), ≤ αM máx.
 *   Vs  = [αvs·(qt − σv)/pa]^0,5, αvs = 10^(0,55·Ic + 1,68)        G₀ = ρ·Vs², ρ = γ/g
 *   Es  = 0,015·10^(0,55·Ic + 1,68)·(qt − σv)       Dr = 100·√(Qtn/CDr), ≤ 100
 *   Kc  = 1 (Ic ≤ 1,70) ou o polinômio de 5º grau de Robertson (2022), Ic ≤ 4,06; Qtn,cs = Kc·Qtn
 *   φ'  = φ'cv + 15,84·log Qtn,cs − 26,88, ≥ φ'cv   ψ = 0,56 − 0,33·log Qtn,cs
 *   Su  = (qt − σv)/Nkt (Nkt fixo ou 10,5 + 7·log Fr; ou Δu₂/Nu)   St = Ns/Fr
 *   OCR = kOCR·Qtn, kOCR fixo ou [Qtn^0,20/(0,25·(10,5 + 7·log Fr))]^1,25
 *   K₀  = (1 − sen φ'p)·OCR^(sen φ'p)              φ'p = 29,5·Bq^0,121·(0,256 + 0,336·Bq + log Qt1)
 *   su(liq)/σ'v = fs/σ'v (Ic > 3) ou 0,0007·e^(0,084·Qtn,cs) + 0,3/Qtn,cs, limitada (escolha 14)
 *   e   = (Gs·γw − γ)/(γ − γw)   w = 100·e/Gs   γd = Gs·γw/(1 + e)   n = e/(1 + e)
 *
 * O que a folha e as fontes não fixam, ou fixam diferente do que o CPeT-IT calcula, saiu do
 * gabarito 2609350 (dez verticais, 18.711 leituras, CPeT-IT v3.9.7.4) e está aqui com a evidência:
 * a fração das 18.711 linhas dentro da tolerância do §5.3 com a escolha e com a alternativa, e
 * quantas linhas a regra muda (tarefa 311.5). O que vem só do dado do CPeT-IT, sem fonte publicada,
 * vai marcado «COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA», com a regra que o RT assina
 * entre colchetes — uma das `REGRAS_A_ASSINAR_CORRELACOES`, que a conformidade leva ao RT —, como em
 * `classificacao.ts`.
 *
 *  1. APLICABILIDADE PELA ZONA SBTn, E SÓ POR ELA — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE
 *     PUBLICADA — A ASSINAR PELO RT [cor.aplicabilidade-pela-zona] (a folha diz «SBTn … ou Ic > / < Ic
 *     de corte»): argilosas (1, 2, 3, 4 e 9) têm Nkt, Su, Su/σ'v, kOCR, OCR, St e K₀; arenosas (5 a 8)
 *     têm Dr, φ', Es e ψ; sem zona (0, Fr < 0,1 %), nenhuma delas — nem k e cₕ (o gabarito dá 0 nas 99
 *     leituras sem zona; com k nelas, 99,47 %). O «ou» do Ic de corte não aparece: as 6 leituras SBTn 5
 *     com Ic > 2,60 só têm as contas de areia, as 203 SBTn 4 com Ic ≤ 2,60 só as de argila, e as 101
 *     sem zona com Ic ≤ 2,60 nenhuma (Su pela zona: 100 %; «zona ou Ic > 2,60»: 99,97 %; Dr «zona ou
 *     Ic < 2,60»: 97,97 %). Por isso o Ic de corte e o ΔIc não entram aqui (nem na classificação, que
 *     segue os ábacos): com outro ΔIc — ajuste do engenheiro que o gabarito não exercita — o motor
 *     calcula como com 0, e `processarVertical` avisa (a condição do ΔIc, `conformidade.ts`). A
 *     su(liq)/σ'v vale em toda zona (escolha 14).
 *  2. N60 SOBRE qt, com pa = 101,325 kPa: 100 % (a folha escreve qc: com o qc da média, 77,30 %;
 *     com o qc da leitura, 74,25 %; qt com pa = 100, 92,25 %). É o (qt/pa)/N60 de Robertson &
 *     Cabal (2012). O CPeT-IT exporta o N60 arredondado; o motor devolve a conta (o relatório
 *     formata com 0 casas).
 *  3. M: o ramo Ic ≤ 2,20 usa 0,0188 — o de Robertson (2009) —, não o 0,03 da folha (com 0,03,
 *     92,91 %). O αM máximo (`alfaMMax`, o `aMax` = 8 do `.cpt`) limita os DOIS ramos — COMPORTAMENTO COPIADO
 *     DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [cor.alfa-m-maximo] (a folha só tem o teto 14
 *     do ramo Ic > 2,20): 99,77 %; com o teto só no ramo argiloso, 83,23 %; sem o αM máximo, 53,75 %. O
 *     teto muda 3.125 leituras do ramo Ic ≤ 2,20 (0,0188·10^(…) > 8) e 5.557 do ramo Ic > 2,20
 *     (Qtn > 8). O `Malpha` = 4 do `.cpt` não entra (o α é automático). Ficam fora (43 linhas): 23 de
 *     superfície sem Ic (fs = 0), em que o CPeT-IT faz a conta com Ic = 0 — aqui é NaN —; 17 do CPTu09
 *     (18,81–18,97 m, Qtn < 2) e 2 de superfície (CPTu02 0,95 m, CPTu10 0,87 m) em que o gabarito dá
 *     M = 4·σ'v (o `Malpha` como piso?), sem fonte, que não entra (o cₕ herda as 13 do CPTu09 e uma
 *     rente, CPTu10 17,37 m); e a do −9999 que o CPeT-IT conta como zero na média (CPTu03 0,02 m,
 *     `basico.ts`).
 *  4. G₀ E Vs DE ROBERTSON (2009): Vs = [αvs·(qt − σv)/pa]^0,5 e G₀ = ρ·Vs², com ρ = γ/g (g = 9,81)
 *     e pa = 101,325 kPa: G₀ 99,87 % e Vs 99,86 %. O G₀ da folha, (qt − σv)·0,0188·10^(…), é essa
 *     conta com ρ/pa trocado por 0,0188 (γ ≈ 18,7 kN/m³): 0,52 % (e o Vs dele, 1,09 %); com
 *     pa = 100, 0,08 %. Ficam fora as de superfície sem Ic (22 no G₀, 23 no Vs; como no M), as 2
 *     de Ic acima de 4,06, que o CPeT-IT corta, e a do −9999 (as duas em `basico.ts`).
 *  5. Es: a fórmula publicada, sem fator de grau de carga: 100 % (× 0,25, 70,63 %). O `Es_Loading`
 *     do `.cpt` vale 0 — um índice da lista de graus de carga do CPeT-IT, não um fator (com ele, Es
 *     seria zero) —, e com ele a conta é a da folha. A premissa `esGrauCarga` (0,25) não entra no
 *     motor 1.0.0: ele só é homologável com ela no padrão.
 *  6. Dr COM TETO DE 100 % — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [cor.dr-teto]: 100 % (sem teto, 99,91 %, 17 fora); a conta passa de 100 em 20 leituras.
 *  7. kOCR — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [cor.kocr-coluna]: a coluna «Kocr» do CPeT-IT só tem a conta com o kOCR automático (`AutoOCR`,
 *     falso no `.cpt`); com o kOCR fixo ela sai 0 nas 18.711 linhas — aqui NaN —, e o OCR usa o fixo
 *     (OCR = 0,33·Qtn: 100 %). O Nkt é diferente: fixo (`AutoNkt` falso), a coluna mostra o 14.
 *  8. K₀ COM O φ' DE PICO, e onde ele não se aplica (Bq fora de 0,1–1) o φ' de pico padrão —
 *     COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [cor.k0-phi-pico-padrao]: a premissa `phiPicoPadrao`, o `Default_Peak_Phi` = 0 do `.cpt`, que dá
 *     K₀ = (1 − 0)·OCR⁰ = 1 nas 1.511 leituras argilosas sem φ'p: 100 % (com φ' = φ'cv no lugar do φ'p,
 *     31,79 %; com `phiPicoPadrao` = null, K₀ NaN nessas 1.511, 91,92 %).
 *  9. φ' DE PICO ENTRE 20° E 40° — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 *     PELO RT [cor.phi-pico-limites] —, em qualquer zona com 0,1 < Bq < 1 (a faixa de Bq é a da folha):
 *     o teto é do CPeT-IT — as 1.188 leituras com a conta acima de 40° (1.001 delas entre 40° e 45°)
 *     saem 40 —, e o piso também: as 10 leituras com a conta abaixo de 20° saem 20. Com os dois, 100 %;
 *     sem limites, 94,01 %; só o teto, 99,95 %; só o piso, 94,07 %; só nas argilosas, 99,08 %.
 * 10. φ' = φ'cv + 15,84·log Qtn,cs − 26,88 — Robertson (2022), eq. 6; Robertson & Cabal (2022),
 *     p. 54 —, só nas zonas 5 a 8: 15,84 = 48 × 0,33 e 26,88 = 48 × 0,56, é φ' = φ'cv − 48·ψ com o
 *     ψ = 0,56 − 0,33·log Qtn,cs (p. 52), que também confere em 100 %. O 15,94 da folha do CPeT-IT é erro de
 *     digitação (com ele, 73,88 %). O PISO NO φ'cv — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE
 *     PUBLICADA — A ASSINAR PELO RT [cor.phi-piso-cv]: 100 %; sem ele, 97,26 % (as 612 leituras
 *     arenosas em que a conta fica abaixo de φ'cv saem φ'cv).
 * 11. w, e, γd e n (Barounis & Philpot, solo saturado) só com z ≥ NA, a leitura no NA incluída —
 *     COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *     [cor.saturado-desde-o-na] (a fonte é para solo saturado; que a leitura no NA entra, é do
 *     gabarito): 100 % (z > NA, 99,95 %: as 9 leituras exatamente no NA; em toda leitura, 97,95 %).
 *     e ≤ 0 não existe: NaN.
 * 12. A resistência ao cisalhamento é o Su nas argilosas e σ'v·tan φ' nas arenosas: 100 %.
 * 13. Kc DE ROBERTSON (2022), eq. 4a — Qtn,cs = Kc·Qtn (eq. 2): Kc = 1 até Ic 1,70 («when Ic < 1.7,
 *     Kc = 1.0»; com o 1,64 de Robertson & Wride, 98,20 %) e, acima, o polinômio de 5º grau em Ic,
 *     sem forçar Kc ≥ 1 (logo acima de 1,70 ele dá 0,99, como as 12 leituras do gabarito ali).
 *     · Os coeficientes — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *       [cor.kc-coeficientes]. O artigo imprime `KC_ROBERTSON_2022_IMPRESSO` — 1,8346; −23,673; 124,02;
 *       −320,616; 405,821; −199,97 —, e com eles o Kc confere em 89,74 % das 18.711 linhas (89,89 % das
 *       18.679 com Ic): o polinômio é mal condicionado (em Ic = 3 a meia unidade de 124,02 vale ±0,135
 *       no Kc). O motor usa `KC_ROBERTSON_2022` — 1,834601; −23,672838; 124,020769; −320,61645;
 *       405,821364; −199,972219 —, estimados contra o Kc do gabarito por um ajuste em caixa: cada
 *       coeficiente preso a ±0,9 meia unidade da última casa impressa. A função objetivo do ajuste
 *       não ficou registrada e o script dele não está no repositório: o que se confere é o
 *       resultado, 99,82 % das 18.711 linhas (99,995 % das 18.679 com Ic; fora, as 32 de superfície
 *       sem Ic, em que o CPeT-IT dá Kc = 1, e a do −9999). Que cada um arredonde para o impresso (o
 *       `298.43` confere) é a restrição do ajuste, não evidência independente — e um deles,
 *       −320,61645, está na borda da caixa. Kc(2) = 1,3127 e Kc(3) = 8,8128 (com os impressos,
 *       1,3072 e 8,7838).
 *     · O Ic entra limitado a 4,06 — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 *       PELO RT [cor.kc-ic-teto]: o CPeT-IT publica Ic = 4,06 no «Basic» nas 2 leituras em que a
 *       conta passa disso (`basico.ts`; a coluna Ic do motor não tem o teto) e dá o Kc de 4,06 nelas
 *       (sem o limite, 99,81 %). O artigo (Robertson 2022, doi:10.1139/cgj-2020-0657, eq. 4a)
 *       restringe a correção — ela vale «for Ic ≤ 3.0» e «should not be extended beyond Ic = 3.0» —,
 *       mas o CPeT-IT a aplica até 4,06 e em toda zona (as 7.650 leituras com Ic > 3 conferem).
 *     · O Qtn,cs não tem teto: as 2.670 leituras com Qtn,cs > 100 (até 576,26) conferem; a
 *       premissa `qtncsLimite` (o «Qtn,cs limit» 100 do `.cpt`) não age em nenhuma das 27 colunas,
 *       e o gabarito não mostra onde ela age.
 * 14. A COLUNA «Residual Shear Strength» É su(liq)/σ'v, A RAZÃO DE RESISTÊNCIA LIQUEFEITA DE
 *     ROBERTSON (2022) — não o Su(rem) = fs em kPa da folha (Ic > 3 com fs: 59,09 %). Em toda zona:
 *     · Ic > 3: su(liq)/σ'v = fs/σ'v (eq. 7: «the remolded undrained shear strength is approximately
 *       equal to the measured CPT sleeve friction»). Que o fs é o da LEITURA, não o da média —
 *       COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 *       [cor.suliq-fs-da-leitura]: as 7.650 leituras com Ic > 3; com o fs da média, 91,50 % da coluna;
 *     · Ic ≤ 3, zonas 5 a 8: a eq. 5, 0,0007·e^(0,084·Qtn,cs) + 0,3/Qtn,cs, limitada por tan φ'. O
 *       artigo diz que a eq. 5 vale até Qtn,cs 80, «after which the drained shear strength ratio will
 *       typically control»; o mín com tan φ' — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA —
 *       A ASSINAR PELO RT [cor.suliq-tan-phi] — muda 2.062 leituras: eq. 5 pura, 88,97 %;
 *       «Qtn,cs < 80 ? eq. 5 : tan φ'», 99,09 %;
 *     · Ic ≤ 3, demais zonas (sem φ'): a eq. 5 com Qtn,cs ≤ 80, o limite de validade — COMPORTAMENTO
 *       COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [cor.suliq-qtncs-80] —, que muda
 *       1.207 leituras: eq. 5 pura, 93,53 %. É o 0,58 das argilas rijas (eq. 5 em 80).
 *     Com a regra inteira, 99,973 % (as 5 fora ficam a 0,0050, meia unidade); separando areia e
 *     argila pelo Ic de corte em vez da zona, 99,05 %. O piso do artigo (Qtn,cs < 20: su(liq)/σ'v
 *     = 0,02, ou su(liq) = 1 kPa com σ'v < 50 kPa) não entra porque não é alcançável neste ramo:
 *     Ic ≤ 3 exige 3,47 − log Qtn ≤ 3, isto é, Qtn ≥ 10^0,47 = 2,951, e o menor Kc·Qtn com Ic ≤ 3 é
 *     Kc(3)·2,951 = 26,0 (Fr = 0,060 %); o menor Qtn,cs do gabarito com Ic ≤ 3 é 27,99.
 *
 * O QUE NÃO CONFERE (fica fora do PDF e do Portal até ser explicado — o `298.40` lista):
 *
 *  · n (porosidade) — a coluna do CPeT-IT não é a porosidade n = e/(1 + e) que Barounis &
 *    Philpot (2017, eq. 8) definem (4,00 m: 21,46 com e = 3,06; chega a 6.253,93, CPTu02 0,95 m).
 *    A forma empírica n = 16,685·Fr·(1 + Bq)^1,2/Qt1^0,3 abaixo do NA e −1 acima a reproduz em
 *    99,995 % das linhas do gabarito (o `#NUM!` é Qt1 negativo elevado a 0,3), sem fonte
 *    publicada nenhuma — por isso NÃO está implementada. O motor dá a
 *    definição, n = e/(1 + e), e a coluna fica `nao_confere` na conformidade (`conformidade.ts`).
 *
 * As premissas `icCorte`, `deltaIc`, `esGrauCarga`, `qtncsLimite` e `q` não têm efeito no motor
 * 1.0.0 (1, 5, 13). Fora isso, a conta é a fórmula: o que dá número fica; o que não dá — log de
 * valor ≤ 0, divisão por zero, raiz de negativo — é NaN, nunca Infinity, e nada lança.
 */
import type { Basico } from './basico';
import type { Premissas, RegraAAssinar, Serie } from './tipos';
import { PA_KPA } from './unidades';

/** As grandezas das correlações (a aba «Estimations» do CPeT-IT mais N1(60)). */
export type GrandezaCorrelacao =
  | 'k' | 'ch' | 'n60' | 'n160' | 'm' | 'dr' | 'phi' | 'es' | 'g0' | 'nkt' | 'su' | 'suRazao'
  | 'kocr' | 'ocr' | 'vs' | 'psi' | 'k0' | 'st' | 'phiPico' | 'resistencia' | 'kc' | 'qtncs' | 'suR'
  | 'gama' | 'w' | 'e' | 'gamaD' | 'porosidade';

/** As correlações de uma vertical: uma série por grandeza, uma posição por leitura. */
export type Correlacoes = Record<GrandezaCorrelacao, Serie>;

const GRANDEZAS_DAS_CORRELACOES: readonly GrandezaCorrelacao[] = [
  'k', 'ch', 'n60', 'n160', 'm', 'dr', 'phi', 'es', 'g0', 'nkt', 'su', 'suRazao', 'kocr', 'ocr', 'vs',
  'psi', 'k0', 'st', 'phiPico', 'resistencia', 'kc', 'qtncs', 'suR', 'gama', 'w', 'e', 'gamaD', 'porosidade',
];

/** Aceleração da gravidade, m/s² (ρ = γ/g, escolha 4). */
const GRAVIDADE = 9.81;

/** O Ic que separa os dois ramos do αM (a folha). */
const IC_DO_ALFA_M = 2.2;

/** O teto do αM do ramo argiloso, αM = mín(Qtn, 14) (a folha); o αM máximo vale depois (escolha 3). */
const ALFA_M_ARGILOSO_MAXIMO = 14;

/** Os limites do k (a folha). */
const IC_K_MINIMO = 1;
const IC_K_DIVISA = 3.27;
const IC_K_MAXIMO = 4;

/**
 * DIAGNÓSTICO — NÃO USAR NO FLUXO OFICIAL: os coeficientes do Kc de Robertson (2022, eq. 4a), do Ic⁵
 * ao termo constante, COMO IMPRESSOS no artigo (escolha 13), para comparar; o motor usa
 * `KC_ROBERTSON_2022`. Com eles, 89,74 % das 18.711 linhas (89,89 % das 18.679 com Ic).
 */
export const KC_ROBERTSON_2022_IMPRESSO: readonly number[] = Object.freeze([
  1.8346, -23.673, 124.02, -320.616, 405.821, -199.97,
]);

/**
 * Os coeficientes do Kc que o motor usa — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA —
 * A ASSINAR PELO RT [cor.kc-coeficientes]: os de Robertson (2022, eq. 4a) com mais casas, estimados
 * contra o gabarito por um ajuste em caixa, cada um preso a ±0,9 meia unidade da última casa
 * impressa (−320,61645 está na borda; a função objetivo não ficou registrada; escolha 13). Com eles,
 * 99,82 % das 18.711 linhas (99,995 % das 18.679 com Ic).
 */
export const KC_ROBERTSON_2022: readonly number[] = Object.freeze([
  1.834601, -23.672838, 124.020769, -320.61645, 405.821364, -199.972219,
]);

/** Até este Ic, Kc = 1 (Robertson 2022: «when Ic < 1.7, Kc = 1.0»; escolha 13). */
const IC_KC_LIMIAR = 1.7;

/**
 * O Ic entra no Kc limitado a este valor, como no CPeT-IT — COMPORTAMENTO COPIADO DO CPeT-IT, SEM
 * FONTE PUBLICADA — A ASSINAR PELO RT [cor.kc-ic-teto]; o artigo não passa de Ic 3,0 (escolha 13).
 */
const IC_KC_TETO = 4.06;

/** Acima deste Ic, su(liq)/σ'v = fs/σ'v (Robertson 2022, eq. 7; escolha 14). */
const IC_SU_LIQ_REMOLDADO = 3;

/**
 * O limite de validade da eq. 5 de Robertson (2022), Qtn,cs < 80, que fora das areias vira teto do
 * Qtn,cs — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 * [cor.suliq-qtncs-80] (escolha 14).
 */
const QTNCS_SU_LIQ_MAXIMO = 80;

/**
 * O teto da Dr, % — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT
 * [cor.dr-teto] (escolha 6).
 */
const DR_MAXIMA = 100;

/** φ' de Robertson (2022, eq. 6): 15,84 = 48 × 0,33 e 26,88 = 48 × 0,56 (escolha 10). */
const PHI_COEFICIENTE = 15.84;
const PHI_CONSTANTE = 26.88;

/** A faixa de Bq em que vale o φ' de pico (a folha). */
const BQ_PICO_MINIMO = 0.1;
const BQ_PICO_MAXIMO = 1;

/**
 * O piso e o teto do φ' de pico — COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR
 * PELO RT [cor.phi-pico-limites] (escolha 9).
 */
const PHI_PICO_MINIMO = 20;
const PHI_PICO_MAXIMO = 40;

/** O número, ou NaN quando ele é infinito ou não existe; o −0 da aritmética sai 0 (como no básico). */
const finito = (x: number): number => (Number.isFinite(x) ? (x === 0 ? 0 : x) : NaN);

const RAD = Math.PI / 180;

/** Zona SBTn argilosa (1, 2, 3, 4 e 9), onde valem Su, OCR, St e K₀ (escolha 1, copiada do CPeT-IT). */
export function zonaArgilosa(zona: number): boolean {
  return zona === 1 || zona === 2 || zona === 3 || zona === 4 || zona === 9;
}

/** Zona SBTn arenosa (5 a 8), onde valem Dr, φ', Es e ψ (escolha 1, copiada do CPeT-IT). */
export function zonaArenosa(zona: number): boolean {
  return zona >= 5 && zona <= 8;
}

/**
 * O Kc de Robertson (2022, eq. 4a): 1 até Ic 1,70; acima, o polinômio de 5º grau em Ic, com o Ic
 * limitado a 4,06 (escolha 13). `coeficientes` (do Ic⁵ ao termo constante) serve para comparar com
 * os impressos; o padrão são os do motor. NaN sem Ic.
 */
export function kcDeIc(ic: number, coeficientes: readonly number[] = KC_ROBERTSON_2022): number {
  if (Number.isNaN(ic)) return NaN;
  if (ic <= IC_KC_LIMIAR) return 1;
  const x = Math.min(ic, IC_KC_TETO);
  let kc = 0;
  for (const c of coeficientes) kc = kc * x + c; // Horner
  return finito(kc);
}

/** A eq. 5 de Robertson (2022): su(liq)/σ'v = 0,0007·e^(0,084·Qtn,cs) + 0,3/Qtn,cs; NaN se Qtn,cs ≤ 0. */
function suLiqDaEq5(qtncs: number): number {
  return qtncs > 0 ? finito(0.0007 * Math.exp(0.084 * qtncs) + 0.3 / qtncs) : NaN;
}

/**
 * φ' (graus) de Robertson (2022, eq. 6) para um Qtn,cs, com piso no φ'cv (o piso: COMPORTAMENTO
 * COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A ASSINAR PELO RT [cor.phi-piso-cv]; escolha 10); NaN
 * se Qtn,cs ≤ 0.
 */
export function phiDeQtncs(qtncs: number, phiCv: number): number {
  const phi = finito(phiCv + PHI_COEFICIENTE * Math.log10(qtncs) - PHI_CONSTANTE);
  return Number.isNaN(phi) ? NaN : Math.max(phi, phiCv);
}

/** O parâmetro de estado ψ (Robertson 2010) para um Qtn,cs; NaN se Qtn,cs ≤ 0. */
export function psiDeQtncs(qtncs: number): number {
  return finito(0.56 - 0.33 * Math.log10(qtncs));
}

/**
 * k (m/s) pelo Ic (a folha); NaN fora de 1 < Ic ≤ 4. O primeiro ramo vai até Ic = 3,27 inclusive
 * (Robertson & Cabal, «Guide to Cone Penetration Testing»: 1,0 < Ic ≤ 3,27): a folha escreve os
 * dois ramos com «<», e em Ic = 3,27 exato não sobraria ramo nenhum.
 */
function kDeIc(ic: number): number {
  if (ic > IC_K_MINIMO && ic <= IC_K_DIVISA) return 10 ** (0.952 - 3.04 * ic);
  if (ic > IC_K_DIVISA && ic <= IC_K_MAXIMO) return 10 ** (-4.52 - 1.37 * ic);
  return NaN;
}

/**
 * O φ' de pico (graus) da folha (Mayne; Robertson & Cabal 2012), NaN fora de 0,1 < Bq < 1, preso
 * entre 20° e 40° como no CPeT-IT (COMPORTAMENTO COPIADO DO CPeT-IT, SEM FONTE PUBLICADA — A
 * ASSINAR PELO RT [cor.phi-pico-limites]; escolha 9).
 */
function phiPicoDe(bq: number, qt1: number): number {
  if (!(bq > BQ_PICO_MINIMO && bq < BQ_PICO_MAXIMO)) return NaN;
  const phi = finito(29.5 * bq ** 0.121 * (0.256 + 0.336 * bq + Math.log10(qt1)));
  return Number.isNaN(phi) ? NaN : Math.min(Math.max(phi, PHI_PICO_MINIMO), PHI_PICO_MAXIMO);
}

/**
 * Os comportamentos das correlações sem fonte publicada, copiados do CPeT-IT (escolhas 1, 3, 6 a 11,
 * 13 e 14 do cabeçalho), um por rótulo do comentário, com o mesmo id entre colchetes: a conformidade
 * (`conformidade.ts`) os leva ao RT para assinar, e o `298.64` confere que a lista e os rótulos são
 * os mesmos.
 */
export const REGRAS_A_ASSINAR_CORRELACOES: readonly RegraAAssinar[] = Object.freeze(
  [
    {
      id: 'cor.aplicabilidade-pela-zona',
      arquivo: 'correlacoes.ts',
      descricao: "A aplicabilidade segue a zona SBTn, e só ela: argilosas (1, 2, 3, 4 e 9) têm Nkt, Su, Su/σ'v, "
        + "kOCR, OCR, St e K₀; arenosas (5 a 8), Dr, φ', Es e ψ; sem zona, nenhuma delas, nem k e cₕ. O «ou Ic "
        + 'de corte» da folha do CPeT-IT não entra.',
      evidencia: 'Su pela zona: 100 % das 18.711 linhas; «zona ou Ic > 2,60»: 99,97 %; Dr «zona ou Ic < 2,60»: '
        + '97,97 %; k também nas 99 leituras sem zona: 99,47 %.',
    },
    {
      id: 'cor.alfa-m-maximo',
      arquivo: 'correlacoes.ts',
      descricao: 'O αM máximo (8, o aMax do .cpt) limita os dois ramos do M; a folha do CPeT-IT só tem o teto 14 do '
        + 'ramo Ic > 2,20.',
      evidencia: '99,77 %; com o teto só no ramo argiloso, 83,23 %; sem o αM máximo, 53,75 % (muda 3.125 leituras do '
        + 'ramo Ic ≤ 2,20 e 5.557 do ramo Ic > 2,20).',
    },
    {
      id: 'cor.dr-teto',
      arquivo: 'correlacoes.ts',
      descricao: 'A densidade relativa Dr tem teto de 100 %.',
      evidencia: '100 %; sem o teto, 99,91 % (17 leituras fora); a conta passa de 100 em 20 leituras.',
    },
    {
      id: 'cor.kocr-coluna',
      arquivo: 'correlacoes.ts',
      descricao: 'A coluna kOCR só tem a conta com o kOCR automático; com o kOCR fixo (o do .cpt) ela fica sem '
        + 'valor e o OCR usa o fixo.',
      evidencia: 'A coluna Kocr do CPeT-IT vale 0 nas 18.711 linhas com o kOCR fixo; OCR = 0,33·Qtn: 100 %.',
    },
    {
      id: 'cor.k0-phi-pico-padrao',
      arquivo: 'correlacoes.ts',
      descricao: "K₀ com o φ' de pico e, onde ele não se aplica (Bq fora de 0,1–1), com o φ' de pico padrão (a "
        + "premissa phiPicoPadrao, o Default_Peak_Phi = 0 do .cpt, que dá K₀ = 1).",
      evidencia: "100 %; com φ'cv no lugar do φ' de pico, 31,79 %; com phiPicoPadrao = null (K₀ sem valor nas 1.511 "
        + "leituras argilosas sem φ' de pico), 91,92 %.",
    },
    {
      id: 'cor.phi-pico-limites',
      arquivo: 'correlacoes.ts',
      descricao: "φ' de pico entre 20° e 40°, em qualquer zona com 0,1 < Bq < 1.",
      evidencia: '100 %; sem limites, 94,01 %; só o teto, 99,95 %; só o piso, 94,07 %; só nas argilosas, 99,08 % '
        + '(1.188 leituras com a conta acima de 40°, 10 abaixo de 20°).',
    },
    {
      id: 'cor.phi-piso-cv',
      arquivo: 'correlacoes.ts',
      descricao: "φ' (Robertson 2022, eq. 6) com piso no φ'cv, nas zonas 5 a 8.",
      evidencia: "100 %; sem o piso, 97,26 % (612 leituras arenosas com a conta abaixo de φ'cv).",
    },
    {
      id: 'cor.saturado-desde-o-na',
      arquivo: 'correlacoes.ts',
      descricao: 'w, e, γd e n (Barounis & Philpot, solo saturado) só com z ≥ NA, a leitura no NA incluída.',
      evidencia: '100 %; com z > NA, 99,95 % (as 9 leituras exatamente no NA); em toda leitura, 97,95 %.',
    },
    {
      id: 'cor.kc-coeficientes',
      arquivo: 'correlacoes.ts',
      descricao: 'Os coeficientes do Kc de Robertson (2022, eq. 4a) com mais casas que os impressos (1,834601; '
        + '−23,672838; 124,020769; −320,61645; 405,821364; −199,972219), estimados contra o gabarito por um ajuste '
        + 'em caixa, cada um preso a ±0,9 meia unidade da última casa impressa; a função objetivo do ajuste não '
        + 'ficou registrada.',
      evidencia: '99,82 % das 18.711 linhas (99,995 % das 18.679 com Ic); com os coeficientes impressos, 89,74 %.',
    },
    {
      id: 'cor.kc-ic-teto',
      arquivo: 'correlacoes.ts',
      descricao: 'O Ic entra no Kc limitado a 4,06; o artigo não estende a correção além de Ic 3,0, e o CPeT-IT a '
        + 'aplica até 4,06 e em toda zona.',
      evidencia: 'Sem o limite, 99,81 % (as 2 leituras com Ic acima de 4,06); as 7.650 leituras com Ic > 3 conferem.',
    },
    {
      id: 'cor.suliq-fs-da-leitura',
      arquivo: 'correlacoes.ts',
      descricao: "su(liq)/σ'v com Ic > 3 (Robertson 2022, eq. 7) usa o fs da leitura, não o da média móvel.",
      evidencia: 'As 7.650 leituras com Ic > 3 conferem; com o fs da média móvel, a coluna cai para 91,50 %.',
    },
    {
      id: 'cor.suliq-tan-phi',
      arquivo: 'correlacoes.ts',
      descricao: "su(liq)/σ'v nas zonas 5 a 8 com Ic ≤ 3: a eq. 5 de Robertson (2022) limitada por tan φ'.",
      evidencia: "Muda 2.062 leituras: eq. 5 pura, 88,97 %; «Qtn,cs < 80 ? eq. 5 : tan φ'», 99,09 %; a regra "
        + 'inteira da coluna, 99,973 %.',
    },
    {
      id: 'cor.suliq-qtncs-80',
      arquivo: 'correlacoes.ts',
      descricao: "su(liq)/σ'v nas demais zonas com Ic ≤ 3: a eq. 5 de Robertson (2022) com Qtn,cs limitado a 80, "
        + 'o limite de validade da equação.',
      evidencia: 'Muda 1.207 leituras: eq. 5 pura, 93,53 % (é o 0,58 das argilas rijas, a eq. 5 em Qtn,cs = 80).',
    },
  ].map((r) => Object.freeze(r)),
);

/**
 * As correlações de uma vertical, leitura a leitura, a partir do básico e da zona SBTn
 * (`classificar`). Devolve séries novas — a entrada nunca muda — e nunca lança: o que não se
 * aplica ou não existe é NaN.
 */
export function correlacionar(b: Basico, c: { sbtn: Int8Array }, p: Premissas): Correlacoes {
  const total = b.total;
  const r = {} as Correlacoes;
  for (const g of GRANDEZAS_DAS_CORRELACOES) r[g] = new Float64Array(total).fill(NaN);

  for (let i = 0; i < total; i++) {
    const zona = c.sbtn[i];
    const argilosa = zonaArgilosa(zona);
    const arenosa = zonaArenosa(zona);
    const ic = b.ic[i];
    const qtn = b.qtn[i];
    const fr = b.fr[i];
    const svl = b.svl[i];
    const qtKpa = 1000 * b.qt[i];
    const qn = qtKpa - b.sv[i]; // a resistência líquida, kPa
    const potencia = 10 ** (0.55 * ic + 1.68); // αvs de Robertson (2009); M, G₀ e Es

    // k e cₕ só com zona (escolha 1, copiada do CPeT-IT).
    const k = zona === 0 ? NaN : kDeIc(ic);
    // M (escolha 3; o αM máximo nos dois ramos é copiado do CPeT-IT).
    let alfaM = ic > IC_DO_ALFA_M ? Math.min(qtn, ALFA_M_ARGILOSO_MAXIMO) : 0.0188 * potencia;
    alfaM = Math.min(alfaM, p.alfaMMax);
    const m = finito((alfaM * qn) / 1000);
    // Vs e G₀ (escolha 4): Vs² = αvs·(qt − σv)/pa; G₀ = ρ·Vs².
    const vs2 = (potencia * qn) / PA_KPA;
    const vs = finito(Math.sqrt(vs2));
    const g0 = Number.isNaN(vs) ? NaN : finito(((b.gama[i] / GRAVIDADE) * vs2) / 1000);
    // N60 e N1(60) (escolha 2).
    const divisorN = 10 ** (1.1268 - 0.2817 * ic);
    // Kc e Qtn,cs de Robertson (2022), sem teto no Qtn,cs (escolha 13).
    const kc = kcDeIc(ic);
    const qtncs = qtn > 0 ? finito(kc * qtn) : NaN;

    r.k[i] = k;
    r.ch[i] = finito((k * m * 1000) / p.gamaW);
    r.n60[i] = finito(qtKpa / PA_KPA / divisorN);
    r.n160[i] = finito(qtn / divisorN);
    r.m[i] = m;
    r.vs[i] = vs;
    r.g0[i] = g0;
    r.kc[i] = kc;
    r.qtncs[i] = qtncs;
    r.gama[i] = b.gama[i];

    const pico = phiPicoDe(b.bq[i], b.qt1[i]);
    r.phiPico[i] = pico;

    // su(liq)/σ'v de Robertson (2022), em toda zona (escolha 14).
    let phi = NaN;
    if (arenosa) phi = phiDeQtncs(qtncs, p.phiCv);
    // eq. 7: su(rem) = fs — o fs da leitura, como o CPeT-IT (copiado, sem fonte).
    if (ic > IC_SU_LIQ_REMOLDADO) r.suR[i] = finito(b.fs[i] / svl);
    // eq. 5 limitada por tan φ' (copiado do CPeT-IT); Math.min com NaN dá NaN.
    else if (arenosa) r.suR[i] = Math.min(suLiqDaEq5(qtncs), finito(Math.tan(phi * RAD)));
    // eq. 5 com o Qtn,cs no limite de validade, 80 (copiado do CPeT-IT).
    else r.suR[i] = suLiqDaEq5(Math.min(qtncs, QTNCS_SU_LIQ_MAXIMO));

    if (arenosa) {
      r.dr[i] = Math.min(DR_MAXIMA, finito(100 * Math.sqrt(qtn / p.cdr))); // Math.min(…, NaN) é NaN
      r.phi[i] = phi;
      r.es[i] = finito((0.015 * potencia * qn) / 1000);
      r.psi[i] = psiDeQtncs(qtncs);
      r.resistencia[i] = finito(svl * Math.tan(phi * RAD));
    }

    if (argilosa) {
      const nkt = p.nktPorFr ? finito(10.5 + 7 * Math.log10(fr)) : p.nkt;
      const su = p.suPorNu ? finito((b.U2[i] * svl) / p.nu) : nkt > 0 ? finito(qn / nkt) : NaN;
      const kocrAuto = finito((qtn ** 0.2 / (0.25 * (10.5 + 7 * Math.log10(fr)))) ** 1.25);
      const ocr = finito((p.kocrAutomatico ? kocrAuto : p.kocr) * qtn);
      // Sem φ'p, o φ' de pico padrão (`phiPicoPadrao`, copiado do CPeT-IT); null → K₀ NaN (escolha 8).
      const phiK0 = Number.isNaN(pico) ? (p.phiPicoPadrao === null ? NaN : p.phiPicoPadrao) : pico;
      const sen = Math.sin(phiK0 * RAD);
      r.nkt[i] = nkt;
      r.su[i] = su;
      r.suRazao[i] = finito(su / svl);
      r.kocr[i] = p.kocrAutomatico ? kocrAuto : NaN; // a coluna só tem a conta automática (escolha 7)
      r.ocr[i] = ocr;
      r.st[i] = finito(p.ns / fr);
      r.k0[i] = ocr > 0 ? finito((1 - sen) * ocr ** sen) : NaN;
      r.resistencia[i] = su;
    }

    // Barounis & Philpot: solo saturado, z ≥ NA (escolha 11; o NA incluído é copiado do CPeT-IT).
    if (b.z[i] >= p.na) {
      const gama = b.gama[i];
      const e = (p.gs * p.gamaW - gama) / (gama - p.gamaW);
      if (e > 0 && Number.isFinite(e)) {
        r.e[i] = e;
        r.w[i] = (100 * e) / p.gs;
        r.gamaD[i] = (p.gs * p.gamaW) / (1 + e);
        r.porosidade[i] = e / (1 + e);
      }
    }
  }
  return r;
}
